/**
 * BackButton component — shared types and Zod prop schema.
 *
 * Icon-only navigation control for returning to the previous flow/screen.
 * The host supplies the localized accessible name.
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';

export const BackButtonState = z.enum(['Default', 'Disabled']);
export type BackButtonState = z.infer<typeof BackButtonState>;

export const backButtonPropsSchema = z.object({
  accessibilityLabel: z.string().min(1),
  state: BackButtonState.default('Default'),
  testID: z.string().optional(),
});

export type BackButtonProps = z.input<typeof backButtonPropsSchema>;

export const BACK_BUTTON_SIZE = sizing.touchTarget.minimum;
export const BACK_BUTTON_ICON_SIZE = sizing.icon.sm;
export const BACK_BUTTON_PADDING = spacing.s;

/** Fully-rounded so the secondary button reads as a circle. */
export const BACK_BUTTON_RADIUS = radius.rounded;

export const backButtonIconColorForState: Record<BackButtonState, string> = {
  Default: colors.icon.primary,
  Disabled: colors.icon.disabled,
};

/**
 * Filled circular background per state, reusing the Secondary Button fill
 * tokens so the back control stays visually consistent with the design-system
 * secondary button (Figma UI Design System — Secondary icon-only Button).
 */
export const backButtonBackgroundForState: Record<BackButtonState, string> = {
  Default: colors.button.secondary,
  Disabled: colors.button.disabled,
};
